import { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { QueryClient } from '@tanstack/react-query';
import Head from 'next/head';
import { useRouter } from 'next-translate-routes';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';

// utils
import getServerSideTranslations from '../../../src/utils/server-side';
import redirectCanonicalSlug from '../../../src/utils/redirectCanonicalSlug';
import getServerDefaultLayout from '../../../src/utils/getServerDefaultLayout';
import { pageSlugToTitle } from '../../../src/utils/pageSlug';

// Services
import { getContentTranslationProxy } from '../../../src/services/content-translations';

// Components
import PageTransition from '../../../components/templates/PageTransition/PageTransition';
import PageLayout from '../../../components/templates/PageLayout/PageLayout';
import ArticleHeading from '../../../components/molecules/ArticleHeading/ArticleHeading';
import Article from '../../../components/templates/Article/Article';
import ImageBlock from '../../../components/atoms/ImageBlock/ImageBlock';
import RichTextBlock from '../../../components/atoms/RichTextBlock/RichTextBlock';
import CarouselBlock from '../../../components/atoms/CarouselBlock/CarouselBlock';
import KeyFiguresBlock from '../../../components/atoms/KeyFiguresBlock/KeyFiguresBlock';
import BreadCrumb from '../../../components/atoms/BreadCrumb/BreadCrumb';
import CTABlock from '../../../components/molecules/CTABlock/CTABlock';
// Hooks
import useSeoLinks from '../../../src/hooks/useSeoLinks';
// Constants
import { BLOG_SLUG, LOCALE_FR } from '../../../src/constants';

const StickyFooterBlock = dynamic(() => import('../../../components/molecules/StickyFooterBlock/StickyFooterBlock'), { ssr: false });

const renderBlock = ({ data, type }) => {
  switch (type) {
    case 'Bloc media':
      return (
        <ImageBlock
          youtube={data?.youtube}
          url={data?.media?.url}
          description={data?.media?.description}
        />
      );
    case 'Bloc texte riche':
      return (
        <RichTextBlock
          content={data?.content}
        />
      );
    case 'Bloc trois colonnes':
      return (
        <KeyFiguresBlock
          items={data?.columns}
          theme="transparent"
          style={{ width: '100%' }}
        />
      );
    case 'Bloc carousel':
      return (
        <CarouselBlock
          medias={data?.medias}
          theme={data?.theme}
          size={data?.size}
        />
      );
    case 'Sticky Footer':
      return (
        <StickyFooterBlock
          hasPhoneButton={data?.hasPhoneButton}
          residencePhone={data?.residencePhone}
          ctaLabel={data?.ctaLabel}
          ctaLink={data?.ctaLink}
        />
      );
    case 'Bloc CTA':
      return (
        <CTABlock
          title={data?.title}
          subtext={data?.subtext}
          ctaLabel={data?.ctaLabel}
          ctaLink={data?.ctaLink}
          ctaType={data?.ctaType}
        />
      );
    default: return null;
  }
};

function Index({ page, header, footer }) {
  const { asPath, query } = useRouter();
  const { t } = useTranslation();
  const { canonicalUrl } = useSeoLinks({ [LOCALE_FR]: asPath?.substring(1) });
  const breadcrumbItems = [
    {
      name: t('breadcrumb.home'),
      position: 1,
      item: '/',
    },
    {
      name: pageSlugToTitle(BLOG_SLUG),
      position: 2,
      item: `/${BLOG_SLUG}`,
    },
    {
      name: pageSlugToTitle(query.slug),
      position: 3,
      item: null,
    },
  ];

  const slugs = {
    en: null,
  };

  // Data
  const data = useMemo(() => page?.[0]?.data || {}, [page?.[0]]);

  return (
    <>
      <Head>
        <title>{data?.metaTitle}</title>
        <meta name="description" content={data?.metaDescription} />

        <link rel="canonical" href={canonicalUrl} />
      </Head>

      <PageLayout
        header={header[0]?.data}
        footer={footer[0]?.data}
        slugs={slugs}
      >
        <PageTransition>
          <Article
            head={(
              <ArticleHeading
                image={data?.previewMedia}
                title={data?.title}
                category={{
                  label: data?.category?.[0]?.data?.category,
                  href: '/conseils-et-actus',
                }}
              />
            )}
          >
            {data?.blocks?.length ? data?.blocks.map((block, index) => (
              <Fragment key={`${block.type}-${index}`}>
                {renderBlock(block)}
              </Fragment>
            )) : null}

            <BreadCrumb noPadding items={breadcrumbItems} theme="transparent" />
          </Article>
        </PageTransition>
      </PageLayout>
    </>
  );
}

Index.propTypes = {
  page: PropTypes.arrayOf(PropTypes.shape({
    creationTimestamp: PropTypes.number,
    data: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string,
        data: PropTypes.shape({}),
      })),
    }),
  })).isRequired,
  header: PropTypes.arrayOf(PropTypes.shape({
    data: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string,
        data: PropTypes.shape({}),
      })),
    }),
  })).isRequired,
  footer: PropTypes.arrayOf(PropTypes.shape({
    data: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string,
        data: PropTypes.shape({}),
      })),
    }),
  })).isRequired,
};

export async function getServerSideProps({ locale, query }) {
  const translations = await getServerSideTranslations(locale);
  const preview = { preview: query.previewMode ? true : null, contentId: query.contentId || null };

  const queryClient = new QueryClient();
  const { header, footer } = await getServerDefaultLayout(queryClient, locale, query.previewMode);

  let category = {};
  if (!query.previewMode) {
    try {
      category = await queryClient.fetchQuery(
        ['category', { slug: query.category, contentTypeId: 'category', locale }],
        () => getContentTranslationProxy({
          slug: query.category,
          contentTypeId: 'category',
        }, locale)
          .then(async (res) => {
            if (res?.data?.length) { return res?.data; }
            throw new Error('notFound');
          }),
      );
    } catch (error) {
      return {
        notFound: true,
      };
    }
  }

  let page = {};
  try {
    page = await queryClient.fetchQuery(
      ['page', { slug: query.slug, contentTypeId: 'article', locale }],
      () => getContentTranslationProxy({
        slug: !preview.preview ? query.slug : null,
        contentTypeId: 'article',
        ...preview,
      }, locale, query.previewMode)
        .then(async (res) => {
          if (res?.data?.length) {
            const redirect = redirectCanonicalSlug(res?.data?.[0]?.canonicalSlug, query.slug, `/conseils-et-actus/${query.category}`);
            if (redirect?.redirect?.destination) return redirect;

            return res?.data;
          }
          throw new Error('notFound');
        }),
    );
  } catch (error) {
    return {
      notFound: true,
    };
  }

  if (page?.redirect) return page;

  return {
    props: {
      page,
      category,
      header,
      footer,
      ...translations,
    },
  };
}

export default Index;
